import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { setConversation } from '@@/features/conversation';
import { Controller, UseFormReturn } from 'react-hook-form';
import { TiDelete } from 'react-icons/ti';
import {
  LuPaperclip,
  LuMessageSquareText,
  LuBot,
  LuClockArrowUp,
  LuVideo,
} from 'react-icons/lu';
import { useGetTemplateMessagesQuery } from '@@/services/templateMessage';
import { useGetMeQuery } from '@@/services/user';
import SelectInput from '@@/components/Inputs/SelectInput';
import Loader from '@@/components/Loader';
import { RootState } from '@@/store';

export const schema = yup
  .object({
    message: yup.string(),
  })
  .required();

export type MessageFormValues = { message: string; template?: string };

const MessageForm: React.FC<{
  form: UseFormReturn<MessageFormValues>;
  conversationId: string;
  cbSuggestions: () => void;
  onSubmit: (files: FileList | null) => void;
  onDelayedSubmit: (files: FileList | null) => void;
  handleGoToVideoLink: () => void;
  isLoading: boolean;
  isSuccess: boolean;
}> = ({
  form,
  conversationId,
  cbSuggestions,
  onSubmit,
  onDelayedSubmit,
  handleGoToVideoLink,
  isLoading,
  isSuccess,
}) => {
  const { data: me } = useGetMeQuery();
  const { data: templateMessages } = useGetTemplateMessagesQuery();
  const {
    register,
    control,
    formState: { errors },
  } = form;

  const dispatch = useDispatch();
  const trackCurrentConversation = useCallback(
    (message: string) => {
      dispatch(setConversation({ message, id: conversationId }));
      form.setValue('message', message);
    },
    [dispatch, conversationId],
  );

  const message = useSelector(
    (state: RootState) => state.conversation[conversationId]?.message ?? '',
  );

  // Files are owned by local state rather than RHF: RHF treats file inputs
  // as uncontrolled, so `setValue('files', undefined)` does not reliably
  // clear them and stale FileLists were leaking into subsequent sends.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<FileList | null>(null);
  const clearFiles = useCallback(() => {
    setFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (isSuccess) {
      clearFiles();
    }
  }, [isSuccess, clearFiles]);

  const isSubmitDisabled = isLoading || (!message && !files?.length);

  const handleSubmitClick = useCallback(() => {
    onSubmit(files);
  }, [onSubmit, files]);

  const handleDelayedSubmitClick = useCallback(() => {
    onDelayedSubmit(files);
  }, [onDelayedSubmit, files]);

  const [showMessageTemplateSelection, setShowMessageTemplateSelection] =
    useState(false);
  const toggleShowMessageTemplateSelection = useCallback(() => {
    setShowMessageTemplateSelection((prev) => !prev);
  }, [setShowMessageTemplateSelection]);

  return (
    <div className="mx-4">
      <Controller
        name="message"
        control={control}
        render={({ field }) => {
          return (
            <div className="relative w-full">
              <textarea
                {...field}
                value={message}
                onChange={(event) => {
                  trackCurrentConversation(event.target?.value);
                }}
                id="message"
                rows={5}
                placeholder="Nouveau message"
                className="peer block w-full ring-1 ring-gray-200 shadow-sm py-3 px-4 border-slate-200 rounded-md placeholder-transparent text-slate-700 text-sm"
              />

              <label
                id="message"
                htmlFor="message"
                className="absolute pointer-events-none font-main text-slate-300 text-sm -top-7 left-0 transition-all peer-focus:-top-6 peer-focus:left-0 peer-focus:text-sm peer-placeholder-shown:top-3 peer-placeholder-shown:left-3.5 peer-placeholder-shown:text-base"
              >
                Nouveau message
              </label>
              {errors?.message?.message && (
                <p className="italic font-main text-sm text-red-500 m-1">
                  {errors.message.message}
                </p>
              )}
            </div>
          );
        }}
      />

      <div className="flex flex-row items-center justify-between mt-2">
        <div className="flex flex-row items-center space-x-2">
          <label htmlFor="files" className="cursor-pointer">
            <LuPaperclip size={16} className="text-slate-600" />

            <input
              ref={fileInputRef}
              onChange={(event) => {
                setFiles(event.target?.files ?? null);
              }}
              type="file"
              id="files"
              className="hidden"
            />
          </label>

          <button className="" onClick={cbSuggestions}>
            <LuBot size={16} className="text-slate-600" />
          </button>

          {!!templateMessages?.length && (
            <button onClick={toggleShowMessageTemplateSelection}>
              <LuMessageSquareText size={16} />
            </button>
          )}

          {!!me?.videoRoomLink && (
            <button className="text-slate-600" onClick={handleGoToVideoLink}>
              <LuVideo size={16} />
            </button>
          )}
        </div>

        <div className="flex flex-row items-center space-x-2">
          <button
            className="text-slate-600 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isSubmitDisabled}
            onClick={handleDelayedSubmitClick}
          >
            <LuClockArrowUp size={16} />
          </button>
          <button
            onClick={handleSubmitClick}
            disabled={isSubmitDisabled}
            className="p-2 bg-emerald-700 rounded-lg flex flex-row items-center justify-center min-w-[88px] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader className="w-4 h-4 text-emerald-300 animate-spin fill-white" />
            ) : (
              <span className="text-white">Envoyer</span>
            )}
          </button>
        </div>
      </div>

      {!!templateMessages?.length && showMessageTemplateSelection && (
        <SelectInput
          register={register}
          id="template"
          options={[{ id: '', label: 'Sélectionnez un message' }].concat(
            templateMessages.map((message) => ({
              id: message.body,
              label: message.title,
            })),
          )}
          callback={(message: string) => {
            trackCurrentConversation(message);
            form.setValue('template', '');
          }}
        />
      )}

      <div className="my-2">
        {!!files?.length &&
          Array.from(files).map((file, i) => (
            <div key={i} className="flex flex-row items-center">
              <p className="text-slate-700 text-sm">{file.name}</p>
              <button className="ml-4" onClick={clearFiles}>
                <TiDelete className="text-red-500" size={20} />
              </button>
            </div>
          ))}
      </div>
    </div>
  );
};

export default MessageForm;
